import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import DeleteModelPopupComponent from "../common_model_popup/deletemodelpopup.component";
import { QuoteService } from "../../../services/user/quotes.services";
import { handleCopy, handleExcel, handlePDF, handlePrint } from "../../../utils/filedownload";
import { getButtonStyles } from "../../../utility";
import moment from "moment";

const QuotesComponent = () => {
    const navigate = useNavigate();
    const selectedStore = useSelector(state => state?.user?.selectedStoreId);
    const [searchText, setSearchText] = useState("");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [quoteToDelete, setQuoteToDelete] = useState(null);
    const [selectedStatusCard, setSelectedStatusCard] = useState(null);
    const [quotesTitle, setQuotesTitle] = useState("Quotes");
    const rowsPerPage = 5;

    // Fetch Quotes
    useEffect(() => {
        const fetchQuoteData = async () => {
            try {
                setLoading(true);
                const response = await QuoteService.getAllQuotes(selectedStore);
                setData(response);
            } catch (error) {
                handleNotify("Error fetching quotes", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
                setData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchQuoteData();
    }, [selectedStore]);

    // Filter Data
    const filterData = (data) => {
        return data.filter(row =>
            (selectedStatusCard ? row.status?.statusName === selectedStatusCard : true) &&
            (
                row.prefixNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
                row.quoteSubject?.toLowerCase().includes(searchText.toLowerCase()) ||
                row.customerName?.toLowerCase().includes(searchText.toLowerCase()) ||
                // row.user?.username?.toLowerCase().includes(searchText.toLowerCase()) ||
                row.validity?.toLowerCase().includes(searchText.toLowerCase()) ||
                row.createDate?.toLowerCase().includes(searchText.toLowerCase()) ||
                row.status?.statusName?.toLowerCase().includes(searchText.toLowerCase())
            )
        )
    };

    // Pagination
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const filteredData = filterData(data);
    const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const handleEditClick = (item) => {
        navigate('/quote/edit', { state: { quoteId: item?.quoteId } });
    };

    const handleDeleteClick = (quote) => {
        setQuoteToDelete(quote);
        setShowModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (quoteToDelete) {
            try {
                await QuoteService.deleteQuote(quoteToDelete.quoteId);
                const newData = data.filter(quote => quote.quoteId !== quoteToDelete.quoteId);
                setData(newData);
                setShowModal(false);
            } catch (error) {
                handleNotify(error.message || "Error while deleting quote.", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
            }
        }
    };

    const handleAddNewClick = () => {
        navigate('/quote/create');
    };

    const columns = [
        {
            name: "Quote#",
            selector: row => row.prefixNumber,
            sortable: true,
            width: "15%"
        },
        { name: "Subject", selector: row => row.quoteSubject, sortable: true, width: "20%" },
        { name: "Customer Name", selector: row => row.customerName, sortable: true, width: "15%" },
        { name: "Total(₹)", selector: row => row.total, sortable: true, width: "15%" },
        {
            name: "Date",
            selector: (row) => {
                return (
                    <>
                        <div className="d-flex">{`Validity: ${row.validity}`}</div>
                        <div>{`Create Date: ${row.createDate}`}</div>
                        <div className="d-flex">{`Valid Till: ${row.expirationDate}`}</div>
                    </>
                );
            },
            sortable: true,
            width: "20%",
        },
        {
            name: "Status",
            selector: row => row.status?.statusName,
            width: "15%",
            cell: row => {
                const { backgroundColor, textColor } = getButtonStyles(row.status);
                return <button
                    className="btn btn-sm"
                    style={{
                        backgroundColor,
                        color: textColor,
                        borderRadius: 25
                    }}
                >
                    {row.status?.statusName}
                </button>
            }
        },
        {
            name: "Assigned To",
            selector: row => `${row.user?.username} (${row.user?.role?.roleName})`,
            width: "12%"
        },
        {
            name: "Approved",
            width: "15%",
            selector: row => (
                <span className={row.user?.approvalForQuote ? "text-success" : "text-danger"}>
                    {row.user?.approvalForQuote ? "Approved" : "Unapproved"}
                </span>
            )
        },
        {
            name: "Actions",
            width: "10%",
            cell: row => (
                <div className="d-flex gap-2">
                    <button className="btn btn-primary" onClick={() => handleEditClick(row)}>Edit</button>
                    <button className="btn btn-danger" onClick={() => handleDeleteClick(row)}>Delete</button>
                </div>
            )
        }
    ];

    //  Dynamic Status Summary (Like LeadsComponent)
    const quotesByStatus = data.reduce((acc, q) => {
        const statusName = q.status?.statusName || "Unknown";
        if (!acc[statusName]) acc[statusName] = { count: 0, value: 0 };
        acc[statusName].count++;
        acc[statusName].value += q.total;
        return acc;
    }, {});

    const totalQuotes = data.length;
    //const totalValue = data.reduce((sum, q) => sum + q.total, 0);

    // Approved / Unapproved Count
    const approvedCount = data.filter(q => q.user?.approvalForQuote).length;
    const unapprovedCount = totalQuotes - approvedCount;

    // Handle Click on Status Card
    const handleClickStatusCard = (statusName) => {
        setSelectedStatusCard(statusName);
        setQuotesTitle(`${statusName} Quotes`);
        // const status = data.find(q => q.status?.statusName === statusName);
        // const { backgroundColor } = getButtonStyles({ colorId: status?.status?.colorId });
    };

    return (
        <div className="page-wrapper m-0">
            <div className="page-content">
                <div className="page-breadcrumb d-flex align-items-center mb-3">
                    <div className="breadcrumb-title pe-3 text-primary fw-bold fs-4">
                        {quotesTitle}
                    </div>
                    <div className="ms-auto">
                        <button className="btn btn-primary" onClick={handleAddNewClick}>
                            New +
                        </button>
                    </div>
                </div>

                {/*  Dynamic Status Cards */}
                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-6 g-4">
                    {Object.keys(quotesByStatus).map(statusName => {
                        const { count, value } = quotesByStatus[statusName];
                        const percentage = ((count / totalQuotes) * 100).toFixed(2);
                        const { backgroundColor } = getButtonStyles({ colorId: data.find(q => q.status?.statusName === statusName)?.status?.colorId });

                        return (
                            <div key={statusName} className="col" onClick={() => handleClickStatusCard(statusName)}>
                                <div className="card radius-10" style={{ borderLeft: `4px solid ${backgroundColor}` }}>
                                    <div className="card-body">
                                        <h5 className="d-flex" style={{ color: backgroundColor }}>{statusName}</h5>
                                        <h5 className="d-flex">₹ {value.toFixed(2)}</h5>
                                        <div className="d-flex justify-content-between">
                                            <p className="mb-0 font-13 d-flex text-primary">{count} Quotes</p>
                                            <p className="mb-0 font-13 text-primary">{percentage}%</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Approved / Unapproved Card */}
                    <div className="col">
                        <div className="card radius-10 border-start border-0 border-4 border-primary">
                            <div className="card-body">
                                <h5 className="d-flex text-primary">Approved / Unapproved</h5>
                                <h5 className="">{approvedCount} / {unapprovedCount}</h5>
                                <div className="d-flex justify-content-between">
                                    <p className="mb-0 font-13 text-primary">{approvedCount} Approved</p>
                                    <p className="mb-0 font-13 text-primary">{unapprovedCount} Unapproved</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Data Table */}
                <div className="card border-top border-0 border-4 border-primary mt-3 p-4">
                    <div className="card-body">
                        {/* Search & Export */}
                        <div className="d-flex flex-column flex-md-row mb-3">
                            <div className="btn-group mb-3 mb-md-0 me-2">
                                <button className="btn btn-outline-secondary" onClick={() => handleCopy(filteredData)}>Copy</button>
                                <button className="btn btn-outline-secondary" onClick={() => handleExcel(filteredData)}>Excel</button>
                                <button className="btn btn-outline-secondary" onClick={() => handlePDF(filteredData)}>PDF</button>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                            </div>
                            <div className="ms-auto d-flex align-items-center">
                                <span className="fw-bold me-2">Search:</span>
                                <input type="text" className="form-control" value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Search 🔍..." />
                            </div>
                        </div>

                        <div className="table-responsive">
                            <DataTable
                                columns={columns}
                                data={currentData}
                                fixedHeader
                                responsive
                                highlightOnHover
                                sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                progressPending={loading}
                            />
                        </div>

                        {showModal && (
                            <DeleteModelPopupComponent
                                show={showModal}
                                onClose={() => setShowModal(false)}
                                onConfirm={handleDeleteConfirm}
                                title="Confirm"
                                confirmText="Delete"
                                Message="Are you sure you want to delete this quote?"
                            />
                        )}

                        {/* Pagination */}
                        <div className="d-flex mt-4 justify-content-end">
                            <div className="btn-group">
                                <button className="btn btn-outline-secondary"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Prev
                                </button>
                                {Array.from({ length: totalPages }, (_, index) => (
                                    <button
                                        key={index}
                                        className={`btn ${currentPage === index + 1 ? "btn-primary" : "btn-outline-secondary"}`}
                                        onClick={() => handlePageChange(index + 1)}
                                    >{index + 1}</button>
                                ))}
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuotesComponent;